import React, { useEffect, useState } from 'react';
import { createPortal, render } from 'react-dom';
import { IoMdClose } from 'react-icons/io';

const VARIANTS = {
  info: 'bg-blue-100 border-blue-500 text-blue-900',
  success: 'bg-green-100 border-green-500 text-green-900', 
  error: 'bg-red-100 border-red-500 text-red-900'
};

const POSITIONS = {
  topRight: 'top-4 right-4',
  bottomRight: 'bottom-4 right-4'
};

const Toast = ({ 
  heading,
  description,
  variant = 'info',
  position = 'topRight',
  onClose,
  autoClose = true,
  autoCloseTime = 3000
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [timer, setTimer] = useState(null);

  useEffect(() => {
    if (autoClose && isVisible) {
      const timeoutId = setTimeout(() => {
        setIsVisible(false);
        onClose?.();
      }, autoCloseTime);
      
      setTimer(timeoutId);

      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    }
  }, [autoClose, autoCloseTime, onClose, isVisible]);

  if (!isVisible) return null;

  const handleClose = () => {
    if (timer) {
      clearTimeout(timer);
    }
    setIsVisible(false);
    onClose?.();
  };

  return createPortal(
    <div 
      className={`fixed ${POSITIONS[position]} z-50 min-w-[320px] max-w-[420px] 
        ${VARIANTS[variant]} border rounded-lg shadow-lg transition-all duration-300`}
    >
      <div className="p-4 pr-12 relative">
        <button
          onClick={handleClose}
          className="absolute right-2 top-2 p-1 rounded-full hover:bg-black/10 transition-colors"
        >
          <IoMdClose size={20} />
        </button>

        {heading && (
          <h4 className="font-semibold text-lg mb-1">{heading}</h4>
        )}
        {description && (
          <p className="text-sm opacity-90">{description}</p>
        )}
      </div>
    </div>,
    document.body
  );
};

// Helper function to create and manage toasts
const toastContainer = {
  show: ({ heading, description, variant, position, autoClose, autoCloseTime }) => {
    const container = document.createElement('div');
    document.body.appendChild(container);

    const handleClose = () => {
    if(container)
      document.body.removeChild(container);
    };

    const toastProps = {
      heading,
      description,
      variant,
      position,
      autoClose,
      autoCloseTime,
      onClose: handleClose
    };

    const element = <Toast {...toastProps} />;
    render(element, container);

    return handleClose;
  }
};

export default toastContainer;
